const MAX_PRECEDING_TURNS = 6;
const MAX_SNAPSHOT_LENGTH = 16000;

const plainText = (value, limit) => typeof value === "string"
  ? value.slice(0, limit).split("").filter((character) => {
    const code = character.charCodeAt(0);
    return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
  }).join("")
  : "";

// Evidence contains text and location/tool labels only, never rendered data, tool
// arguments, actions, attachments or a system prompt from the conversation entry.
const screenEvidence = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const screen = {};
  const path = plainText(value.path, 256).split(/[?#]/, 1)[0];
  if (path.startsWith("/")) screen.path = path;
  if (typeof value.site_number === "string" || Number.isFinite(value.site_number)) {
    screen.site_number = plainText(String(value.site_number), 128);
  }
  if (Number.isSafeInteger(value.site_id) && value.site_id > 0) screen.site_id = value.site_id;
  const siteName = plainText(value.site_name, 200);
  if (siteName) screen.site_name = siteName;
  const language = plainText(value.language, 16);
  if (/^[a-z]{2,3}(?:-[a-z0-9]+)?$/i.test(language)) screen.language = language;
  return Object.keys(screen).length ? screen : null;
};

const toolEvidence = (value) => Array.isArray(value)
  ? [...new Set(value.filter((name) => typeof name === "string" && /^[a-z][a-z0-9_]{0,79}$/i.test(name)))].slice(0, 20)
  : [];

export const captureFeedbackContext = (history, reviewedIndex) => {
  if (!Array.isArray(history) || !Number.isInteger(reviewedIndex)
    || reviewedIndex < 0 || reviewedIndex >= history.length) return null;

  const turns = history.slice(Math.max(0, reviewedIndex - MAX_PRECEDING_TURNS), reviewedIndex + 1);
  let truncated = reviewedIndex > MAX_PRECEDING_TURNS;
  const messages = [];
  turns.forEach((turn) => {
    const user = plainText(turn?.user, 2000);
    const assistant = plainText(turn?.response, 4000);
    if ((typeof turn?.user === "string" && user.length !== turn.user.length)
      || (typeof turn?.response === "string" && assistant.length !== turn.response.length)) truncated = true;
    const userMessage = { role: "user", content: user };
    const screen = screenEvidence(turn?.screenContext);
    if (screen) userMessage.screen_context = screen;
    messages.push(userMessage, { role: "assistant", content: assistant, tools_used: toolEvidence(turn?.toolsUsed) });
  });

  const snapshot = { version: 1, messages, reviewed_message_index: messages.length - 1, truncated };
  // Remove oldest complete turns first so the reviewed question and answer have
  // priority even when earlier chat replies are long or contain escaped text.
  while (JSON.stringify(snapshot).length > MAX_SNAPSHOT_LENGTH && messages.length > 2) {
    messages.splice(0, 2);
    snapshot.reviewed_message_index = messages.length - 1;
    snapshot.truncated = true;
  }
  while (JSON.stringify(snapshot).length > MAX_SNAPSHOT_LENGTH) {
    messages.forEach((message) => { message.content = message.content.slice(0, Math.floor(message.content.length / 2)); });
    snapshot.truncated = true;
  }
  return snapshot;
};

export const feedbackPayload = (pending) => ({
  audit_id: pending.auditId,
  feedback: pending.type,
  correction: pending.correction,
  session_id: pending.sessionId,
  user_message: pending.userMessage,
  bot_response: pending.botResponse,
  screen_context: pending.screenContext || null,
  context_snapshot: pending.contextSnapshot || null,
});

// Await both queued feedback and a timer-triggered save already in flight.
export const flushFeedbackBeforeRetry = async (pending, inFlight, commit) => {
  const saves = Object.values(inFlight);
  for (const index of Object.keys(pending)) {
    clearTimeout(pending[index].timeoutId);
    saves.push(commit(Number(index)));
  }
  await Promise.all(saves);
};
